// #tag: ADMIN AI ASSISTANT AND VISION CHAT SCREEN
// #purpose: Interactive AI workspace allowing administrators to query system analytics, summarize documents, analyze attachments, and inspect model health.
import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Paperclip, Send, Sparkles, X } from 'lucide-react';
import { createAdminAiPrompt, fetchAdminAi } from '../../api/admin';
import { ApiError } from '../../api/client';
import {
  AI_CHAT_ACCEPT,
  AI_CHAT_MAX_FILES,
  createAttachmentDraft,
  prepareAiChatAttachment,
  revokeAttachmentDrafts,
  type AiChatAttachmentDraft,
} from '../../lib/aiChatAttachments';
import { AdminStatusBadge } from '../../components/admin/AdminPageLayout';
import { LoadingState, ErrorState } from '../../components/states/StateViews';

type ChatAttachmentView = {
  id: string;
  fileName: string;
  kind: 'image' | 'document';
  previewUrl?: string;
};

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  permission?: string;
  permissionLabel?: string;
  status?: string;
  attachments?: ChatAttachmentView[];
  createdAt: string;
};

type PendingAttachment = AiChatAttachmentDraft & {
  file: File;
};

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function AdminAiChatBox({
  permissions,
  enabled,
  model,
}: {
  permissions: Array<{ id: string; label: string }>;
  enabled: boolean;
  model: string;
}) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [permission, setPermission] = useState(permissions[0]?.id ?? 'ai.assistant');
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [error, setError] = useState('');
  const threadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (permissions.length > 0 && !permissions.some((item) => item.id === permission)) {
      setPermission(permissions[0].id);
    }
  }, [permissions, permission]);

  useEffect(() => () => revokeAttachmentDrafts(pendingAttachments), [pendingAttachments]);

  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, error, pendingAttachments.length]);

  const clearPendingAttachments = () => {
    revokeAttachmentDrafts(pendingAttachments);
    setPendingAttachments([]);
  };

  const removePendingAttachment = (id: string) => {
    setPendingAttachments((current) => {
      const target = current.find((item) => item.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return current.filter((item) => item.id !== id);
    });
  };

  const addFiles = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (list.length === 0) return;

    setError('');
    const availableSlots = AI_CHAT_MAX_FILES - pendingAttachments.length;
    if (availableSlots <= 0) {
      setError(`You can attach up to ${AI_CHAT_MAX_FILES} files.`);
      return;
    }

    const nextFiles = list.slice(0, availableSlots);
    try {
      const drafts = nextFiles.map((file) => ({
        ...createAttachmentDraft(file),
        file,
      }));
      setPendingAttachments((current) => [...current, ...drafts]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add file.');
    }
  };

  const send = useMutation({
    mutationFn: async ({ text, attachmentFiles }: { text: string; attachmentFiles: PendingAttachment[] }) => {
      const prepared = await Promise.all(attachmentFiles.map((item) => prepareAiChatAttachment(item.file)));
      return createAdminAiPrompt({
        prompt: text,
        permission,
        attachments: prepared,
      });
    },
    onMutate: ({ text, attachmentFiles }) => {
      const permissionLabel = permissions.find((item) => item.id === permission)?.label ?? permission;
      setMessages((current) => [
        ...current,
        {
          id: `local-user-${Date.now()}`,
          role: 'user',
          content: text,
          permission,
          permissionLabel,
          attachments: attachmentFiles.map((item) => ({
            id: item.id,
            fileName: item.fileName,
            kind: item.kind,
            previewUrl: item.previewUrl,
          })),
          createdAt: new Date().toISOString(),
        },
      ]);
      setInput('');
      clearPendingAttachments();
      setError('');
    },
    onSuccess: (result) => {
      const row = result.prompt;
      setMessages((current) => [
        ...current,
        {
          id: row.id,
          role: 'assistant',
          content: row.response ?? 'No response returned.',
          permission,
          permissionLabel: permissions.find((item) => item.id === permission)?.label ?? permission,
          status: row.status,
          createdAt: row.createdAt,
        },
      ]);
      void qc.invalidateQueries({ queryKey: ['admin-ai'] });
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : 'AI request failed';
      setError(message);
      setMessages((current) => [
        ...current,
        {
          id: `local-error-${Date.now()}`,
          role: 'assistant',
          content: message,
          status: 'failed',
          createdAt: new Date().toISOString(),
        },
      ]);
    },
  });

  const submit = () => {
    const text = input.trim();
    if ((!text && pendingAttachments.length === 0) || send.isPending) return;
    send.mutate({
      text: text || 'Review the attached files and summarize the key points for admin action.',
      attachmentFiles: pendingAttachments,
    });
  };

  return (
    <article className="wfx-admin-panel glass-surface wfx-admin-ai-chat wfx-admin-ai-chat--solo">
      <header className="wfx-admin-ai-chat-head">
        <div>
          <h2><Sparkles size={18} aria-hidden="true" /> AI Assistant</h2>
          <p className="wfx-muted">
            {enabled ? `Connected · ${model}` : 'Offline — prompts will be saved without live AI'}
          </p>
        </div>
      </header>

      <div ref={threadRef} className="wfx-admin-ai-chat-thread" aria-live="polite">
        {messages.length === 0 ? (
          <div className="wfx-admin-ai-chat-empty">
            <Sparkles size={28} aria-hidden="true" />
            <strong>Ask anything about your admin console</strong>
            <span className="wfx-muted">
              Choose a permission scope, attach docs or images for context, then ask your question.
            </span>
          </div>
        ) : (
          messages.map((message) => (
            <div key={message.id} className={`wfx-admin-ai-chat-bubble is-${message.role}`}>
              <div className="wfx-admin-ai-chat-bubble-meta">
                <span>{message.role === 'user' ? 'You' : 'Assistant'}</span>
                {message.permissionLabel && <span className="chip-pill">{message.permissionLabel}</span>}
                {message.status && message.role === 'assistant' && <AdminStatusBadge status={message.status} />}
              </div>
              {message.attachments && message.attachments.length > 0 && (
                <div className="wfx-admin-ai-chat-attachments">
                  {message.attachments.map((attachment) => (
                    <div key={attachment.id} className="wfx-admin-ai-chat-attachment">
                      {attachment.previewUrl ? (
                        <img src={attachment.previewUrl} alt={attachment.fileName} />
                      ) : (
                        <div className="wfx-admin-ai-chat-attachment-doc">
                          <FileText size={18} aria-hidden="true" />
                        </div>
                      )}
                      <span>{attachment.fileName}</span>
                    </div>
                  ))}
                </div>
              )}
              <p>{message.content}</p>
            </div>
          ))
        )}
        {send.isPending && (
          <div className="wfx-admin-ai-chat-bubble is-assistant is-loading">
            <div className="wfx-admin-ai-chat-bubble-meta"><span>Assistant</span></div>
            <p>Thinking…</p>
          </div>
        )}
      </div>

      <form
        className="wfx-admin-ai-chat-compose"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <label className="wfx-admin-field wfx-admin-ai-chat-permission">
          <span>Permission scope</span>
          <select value={permission} onChange={(e) => setPermission(e.target.value)} aria-label="Permission scope">
            {permissions.map((item) => (
              <option key={item.id} value={item.id}>{item.label}</option>
            ))}
          </select>
        </label>

        {pendingAttachments.length > 0 && (
          <div className="wfx-admin-ai-chat-attachment-list">
            {pendingAttachments.map((attachment) => (
              <div key={attachment.id} className="wfx-admin-ai-chat-attachment-chip">
                {attachment.previewUrl ? (
                  <img src={attachment.previewUrl} alt="" />
                ) : (
                  <FileText size={16} aria-hidden="true" />
                )}
                <div>
                  <strong>{attachment.fileName}</strong>
                  <span className="wfx-muted">{formatBytes(attachment.size)}</span>
                </div>
                <button
                  type="button"
                  className="ghost-icon-button"
                  aria-label={`Remove ${attachment.fileName}`}
                  onClick={() => removePendingAttachment(attachment.id)}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="wfx-admin-ai-chat-input-row">
          <input
            ref={fileInputRef}
            type="file"
            className="sr-only"
            multiple
            accept={AI_CHAT_ACCEPT}
            onChange={(e) => {
              if (e.target.files) void addFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            className="secondary-action compact wfx-admin-ai-chat-attach-btn"
            aria-label="Attach files"
            disabled={send.isPending || pendingAttachments.length >= AI_CHAT_MAX_FILES}
            onClick={() => fileInputRef.current?.click()}
          >
            <Paperclip size={16} aria-hidden="true" />
          </button>
          <label className="wfx-admin-field wfx-admin-ai-chat-input">
            <span className="sr-only">Message</span>
            <textarea
              rows={3}
              className="wfx-admin-textarea"
              value={input}
              placeholder="Ask a question or attach files for context…"
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
            />
          </label>
        </div>

        {error && <p className="wfx-admin-form-error">{error}</p>}

        <div className="wfx-admin-ai-chat-actions">
          <span className="wfx-muted">
            Attach up to {AI_CHAT_MAX_FILES} files · images, PDF, or text docs · Enter to send
          </span>
          <button
            type="submit"
            className="primary-action compact"
            disabled={(!input.trim() && pendingAttachments.length === 0) || send.isPending}
          >
            <Send size={16} aria-hidden="true" />
            {send.isPending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </form>
    </article>
  );
}

export function AdminAiScreen() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-ai'],
    queryFn: () => fetchAdminAi({ limit: 1 }),
  });

  if (isLoading && !data) return <LoadingState label="Loading AI assistant…" />;
  if (isError || !data) return <ErrorState message="Could not load AI assistant." onRetry={() => void refetch()} />;

  return (
    <div className="wfx-admin-ai-page">
      <AdminAiChatBox
        permissions={data.permissions}
        enabled={data.enabled}
        model={data.model}
      />
    </div>
  );
}
